import {
  Component,
  AfterViewInit,
  ElementRef,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import { Chart, ChartConfiguration, ScriptableContext } from "chart.js/auto";
import moment from "moment";
import { Subscription } from "rxjs";

import { HISChartsService } from "../../dashboards/shared/his-charts.service";
import { DLService } from "../../shared/dl.service";
import { CoreService } from "../../core/shared/core.service";
import { HomeBLServiceService } from "../Services/Home/home-blservice.service";
import { TranslateService } from "@ngx-translate/core";

import { HomeDashboardResponse } from "../Models/Home/HomeDashboardResponse";
import { ChartResponse } from "../Models/Home/ChartResponse";
import { MedicalStatisticsChartResponse } from "../Models/Home/MedicalStatisticsChartResponse";
import { PatientFlowStatusResponse } from "../Models/Home/PatientFlowStatusResponse";
import { BAR_CYCLE, CHART_AREA_BOTTOM, CHART_AREA_TOP, CHART_COLORS, CHART_GRID, CHART_TICK, barGradientStops } from "@app/dashboards/shared/chart-palette";

@Component({
  selector: "my-app",
  templateUrl: "./dashboard-home.html",
  standalone: false,
})
export class DashboardHomeComponent implements AfterViewInit, OnDestroy {

 

  constructor(
    public hisCharts: HISChartsService,
    public dlService: DLService,
    public coreService: CoreService,
    public homeBLServiceService: HomeBLServiceService,
    private translate: TranslateService,
  ) {}
 //==================================================================Variables
 // ===================== Color Palette (Unified)
  private readonly chartColors = CHART_COLORS;
  private readonly gridColor = CHART_GRID;
  private readonly tickColor = CHART_TICK;

  // Each bar gets its own vertical gradient, from its top down to the axis.
  private barGradient = (ctx: ScriptableContext<"bar">): CanvasGradient | string => {
    const [top, bottom] = barGradientStops(BAR_CYCLE[ctx.dataIndex % BAR_CYCLE.length]);
    const { chart } = ctx;
    const yScale = chart.scales["y"];
    if (!chart.chartArea || !yScale) return bottom;
    const yTop = yScale.getPixelForValue(Number(ctx.raw) || 0);
    const yBottom = yScale.getPixelForValue(0);
    if (!isFinite(yTop) || yTop === yBottom) return bottom;
    const gradient = chart.ctx.createLinearGradient(0, yTop, 0, yBottom);
    gradient.addColorStop(0, top);
    gradient.addColorStop(1, bottom);
    return gradient;
  };
  // ===================== Chart Instances
  zoneChart!: Chart;
  deptChart!: Chart;
  medicalChart!: Chart;
  private chartSubs: Subscription[] = [];

  // ===================== ViewChild
  @ViewChild("zoneChart") zoneChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild("deptChart") deptChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild("medicalChart") medicalChartRef!: ElementRef<HTMLCanvasElement>;

  // ===================== Data
  homeDashboardResponse!: HomeDashboardResponse;
  patientFlowStatusResponse!: PatientFlowStatusResponse;
  patientDistributionChartResponse!: ChartResponse[];
  visitDistributionChartResponse!: ChartResponse[];
  medicalStatisticsChartResponse!: MedicalStatisticsChartResponse[];

  currentDate = moment().format("DD-MM-YYYY");
  showCountryMap = true;

  //============================================== Methods
  ngOnInit(): void {
    this.loadHomeDashboardStatistics();
    this.loadPatientDistributionChart();
    this.loadVisitDistributionChart();
    this.loadMedicalStatisticsChart();
    this.loadPatientFlowStatus();
  }

  ngAfterViewInit(): void {
    this.showCountryMap = this.coreService.showCountryMapOnLandingPage;
  }

  // A chart left alive keeps Chart.js's document-wide MutationObserver running, which re-triggers change detection on every DOM change.
  ngOnDestroy(): void {
    this.chartSubs.forEach((s) => s.unsubscribe());
    this.zoneChart?.destroy();
    this.deptChart?.destroy();
    this.medicalChart?.destroy();
  }

  private renderChart(
    canvas: ElementRef<HTMLCanvasElement>,
    chartInstance: Chart | null,
    config: ChartConfiguration,
  ): Chart {
    if (chartInstance) {
      chartInstance.destroy();
    }
    return new Chart(canvas.nativeElement, config);
  }

  loadHomeDashboardStatistics(): void {
    this.homeBLServiceService.getHomeDashboard().subscribe(res => {
      if (res.Succeeded) {
        this.homeDashboardResponse = res.Data;
      }
    });
  }

  loadPatientDistributionChart(): void {
    this.chartSubs.push(this.homeBLServiceService.getPatientDistributionChart().subscribe(res => {
      if (res.Succeeded && res.Data?.length) {
        this.patientDistributionChartResponse = res.Data;
        this.renderZoneChart();
      }
    }));
  }

  loadVisitDistributionChart(): void {
    this.chartSubs.push(this.homeBLServiceService.getVisitDistributionChart().subscribe(res => {
      if (res.Succeeded && res.Data?.length) {
        this.visitDistributionChartResponse = res.Data;
        this.renderDeptChart();
      }
    }));
  }

  loadMedicalStatisticsChart(): void {
    this.chartSubs.push(this.homeBLServiceService.getMedicalStatisticsChart().subscribe(res => {
      if (res.Succeeded && res.Data?.length) {
        this.medicalStatisticsChartResponse = res.Data;
        this.renderMedicalChart();
      }
    }));
  }

  loadPatientFlowStatus(): void {
    this.homeBLServiceService.getPatientFlowStatus().subscribe(res => {
      if (res.Succeeded) {
        this.patientFlowStatusResponse = res.Data;
      }
    });
  }

  // ===================== Charts Rendering
  private renderZoneChart(): void {
    const labels = this.patientDistributionChartResponse.map(x => x.Name);
    const values = this.patientDistributionChartResponse.map(x => x.Count);

    this.zoneChart = this.renderChart(this.zoneChartRef, this.zoneChart, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: this.translate.instant("Home.Charts.Patients"),
            data: values,
            backgroundColor: this.barGradient,
            hoverBackgroundColor: this.barGradient,
            borderWidth: 0,
            borderRadius: 6,
            borderSkipped: false,
            maxBarThickness: 28,
            categoryPercentage: 0.6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { display: false }, border: { display: false }, ticks: { color: this.tickColor } },
          y: {
            beginAtZero: true,
            grid: { color: this.gridColor },
            border: { display: false, dash: [4, 4] },
            ticks: { color: this.tickColor },
          },
        },
        animation: { duration: 2000 },
      },
    });
  }

  private renderDeptChart(): void {
    const labels = this.visitDistributionChartResponse.map(x => x.Name);
    const values = this.visitDistributionChartResponse.map(x => x.Count);

    this.deptChart = this.renderChart(this.deptChartRef, this.deptChart, {
      type: "pie",
      data: {
        labels,
        datasets: [
          {
            label: this.translate.instant("Home.Charts.Appointments"),
            data: values,
            backgroundColor: [
              this.chartColors.teal,
              this.chartColors.coral,
              this.chartColors.purple,
              this.chartColors.navy,
              this.chartColors.slate,
            ],
            borderColor: "#fff",
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: "top" } },
        animation: { duration: 2000 },
      },
    });
  }

  private renderMedicalChart(): void {
    const sorted = [...this.medicalStatisticsChartResponse].sort((a, b) =>
      a.Year !== b.Year ? a.Year - b.Year : a.MonthNumber - b.MonthNumber
    );

    const labels = sorted.map(x => `${x.MonthName} ${x.Year}`);
    const values = sorted.map(x => x.Count);

    this.medicalChart = this.renderChart(
      this.medicalChartRef,
      this.medicalChart,
      {
        type: "line",
        data: {
          labels,
          datasets: [
            {
              label: this.translate.instant("Home.Charts.PatientAdmissions"),
              data: values,
              borderColor: this.chartColors.teal,
              backgroundColor: (ctx: ScriptableContext<"line">) => {
                const area = ctx.chart.chartArea;
                if (!area) return CHART_AREA_TOP;
                const gradient = ctx.chart.ctx.createLinearGradient(0, area.top, 0, area.bottom);
                gradient.addColorStop(0, CHART_AREA_TOP);
                gradient.addColorStop(1, CHART_AREA_BOTTOM);
                return gradient;
              },
              pointBackgroundColor: "#fff",
              pointBorderColor: this.chartColors.teal,
              pointBorderWidth: 2,
              fill: true,
              tension: 0.4,
              pointRadius: 5,
              pointHoverRadius: 7,
              pointHitRadius: 15,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: "top" },
            tooltip: {
              callbacks: {
                label: ctx => `${this.translate.instant("Home.Charts.Admissions")}: ${ctx.raw}`,
              },
            },
          },
          scales: { y: { beginAtZero: true } },
          animation: { duration: 2000 },
        },
      }
    );
  }
}
